import { ColumnDef } from "@tanstack/react-table";
import { EditIcon, TrashAltIcon } from "assets/icons";
import { DragDropChangeValue } from "components";
import { moveElementInArray } from "helpers";
import { useLanguage } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import { t } from "i18next";
import { useMemo, useState } from "react";
import {
  ItemsTableDragDrop,
  RowDragHandleCell,
  Switch,
  TableItemActionsControl
} from "ui";

import { useReorderFaqItems, useUpdateFaqItem } from "../../apis";
import { FAQListItem } from "../../content.types";
import { FAQTableProps } from "./FAQTable.types";

import styles from "./FAQTable.module.scss";

// Rough plain-text excerpt of an HTML (Quill) body for the table cell.
const stripHtml = (html: string) =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();

const FAQTable = ({
  faqItems,
  isLoading,
  isDragDisabled,
  onEdit,
  onDelete,
  setFaqItems
}: FAQTableProps) => {
  const { currentLanguage } = useLanguage();

  const reorderFaqItems = useReorderFaqItems();
  const updateFaqItem = useUpdateFaqItem();

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const onChangeOrder = (value: DragDropChangeValue) => {
    const { fromIndex, toIndex } = value;

    const reordered = moveElementInArray({
      array: faqItems,
      keyForReorder: "sortOrder",
      fromIndex,
      toIndex
    });

    setFaqItems(reordered);

    reorderFaqItems.mutate({
      items: reordered.map((item) => ({
        id: item.id,
        sortOrder: item.sortOrder
      }))
    });
  };

  const columns: Array<ColumnDef<FAQListItem, FAQListItem[]>> = useMemo(
    () => [
      {
        id: "dragHandle",
        header: "",
        enableSorting: false,
        size: 3,
        minSize: 3,
        cell: (cell) => (
          <RowDragHandleCell
            rowId={cell.row.id}
            isDragDisabled={isDragDisabled}
          />
        )
      },
      {
        id: "question",
        header: t("Question"),
        enableSorting: false,
        size: 35,
        minSize: 35,
        cell: ({ row }) => (
          <div className={styles.faqTable_question}>
            {row.original.question}
          </div>
        )
      },
      {
        id: "answer",
        header: t("Answer"),
        enableSorting: false,
        size: 44,
        minSize: 44,
        cell: ({ row }) => (
          <div className={styles.faqTable_answer}>
            {stripHtml(row.original.answer)}
          </div>
        )
      },
      {
        id: "isEnabled",
        header: t("Visible"),
        enableSorting: false,
        size: 14,
        minSize: 14,
        cell: ({ row }) => (
          <Switch
            checked={row.original.isEnabled}
            onChange={(e) =>
              updateFaqItem.mutate({
                id: row.original.id,
                isEnabled: e.target.checked
              })
            }
          />
        )
      },
      {
        accessorKey: "dotsColumn",
        header: () => null,
        size: 4,
        minSize: 4,
        cell: ({ row }) => (
          <TableItemActionsControl
            actionsList={[
              {
                label: "Edit",
                Icon: <EditIcon fill={"#090914"} width={18} height={18} />,
                onClick: () => onEdit(row.original)
              },
              {
                label: "Delete",
                Icon: <TrashAltIcon fill={"#ff0033"} width={18} height={18} />,
                labelColor: "#ff0033",
                onClick: () => onDelete(row.original.id)
              }
            ]}
            rowIndex={row.index}
            setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
            openedItemOptionsIndex={openedItemOptionsIndex}
          />
        ),
        enableSorting: false
      }
    ],
    [faqItems, openedItemOptionsIndex, isDragDisabled, currentLanguage]
  );

  return (
    <ItemsTableDragDrop
      className={styles.faqTable}
      columns={columns}
      data={faqItems}
      isLoading={isLoading}
      skeletonHeight={65}
      emptyListMessage={t("No questions yet")}
      droppableId={"faqItemsList"}
      keyForReorder={"sortOrder"}
      onChange={onChangeOrder}
      isDragDisabled={isDragDisabled}
    />
  );
};

export default FAQTable;
